import { useCallback, useRef, useState } from 'react';
import { StackContainer, StackCard, useStackProgress } from 'stack-on-scroll';
import { CARDS, CardData } from './cards';
import './App.css';

const PORTFOLIO = 'https://isaadahmad.com';
const NPM = 'https://www.npmjs.com/package/stack-on-scroll';
const REPO = 'https://github.com/saadahmad888/stack-on-scroll';

interface Settings {
  offset: number;
  scaleStep: number;
  fadeStep: number;
  rotateStep: number;
  stackFrom: 'top' | 'bottom';
  respectReducedMotion: boolean;
}

const INITIAL: Settings = {
  offset: 28,
  scaleStep: 0.07,
  fadeStep: 0.35,
  rotateStep: 0,
  stackFrom: 'top',
  respectReducedMotion: true,
};

/* ─────────────────────────── card ─────────────────────────── */

function SpecCard({ card, offset }: { card: CardData; offset: number }) {
  const readout = useRef<HTMLSpanElement>(null);

  // Writes straight to the DOM. No state, so no re-render per frame.
  const onProgress = useCallback((progress: number) => {
    if (readout.current) {
      readout.current.textContent = `${Math.round(progress * 100)}%`;
    }
  }, []);

  const ref = useStackProgress<HTMLElement>({ onProgress, offset });

  return (
    <StackCard ref={ref} aria-labelledby={`${card.id}-title`}>
      <article className="card" style={{ ['--accent' as string]: card.accent }}>
        <img className="card__image" src={card.image} alt="" aria-hidden="true" />
        <div className="card__scrim" />

        <div className="card__body">
          <p className="card__kicker mono">{card.kicker}</p>
          <h2 className="card__title" id={`${card.id}-title`}>
            {card.title}
          </h2>
          <p className="card__text">{card.body}</p>
        </div>

        <dl className="card__spec mono">
          {card.spec.map(([term, value]) => (
            <div className="card__specRow" key={term}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>

        <p className="card__readout mono">
          <span ref={readout}>0%</span>
          <span className="card__readoutLabel">covered</span>
        </p>
      </article>
    </StackCard>
  );
}

/* ───────────────────────── controls ───────────────────────── */

interface SliderProps {
  name: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  onChange: (value: number) => void;
}

function Slider({ name, value, min, max, step, suffix = '', onChange }: SliderProps) {
  return (
    <div className="control">
      <label className="mono" htmlFor={`c-${name}`}>
        {name}
      </label>
      <span className="mono control__value">
        {value}
        {suffix}
      </span>
      <input
        id={`c-${name}`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

interface ControlsProps {
  settings: Settings;
  onChange: (patch: Partial<Settings>) => void;
  onReset: () => void;
}

function Controls({ settings, onChange, onReset }: ControlsProps) {
  const [open, setOpen] = useState(true);

  return (
    <aside className={`panel ${open ? 'panel--open' : 'panel--closed'}`}>
      <button
        type="button"
        className="panel__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="panel-body"
      >
        <span className="panel__title mono">Props</span>

        {/* Collapsed, the summary keeps the current values visible. */}
        <span className="panel__summary mono">
          {settings.offset}px · {settings.scaleStep} · {settings.fadeStep}
        </span>

        <span className="panel__chevron" aria-hidden="true">
          {open ? '▾' : '▴'}
        </span>
      </button>

      <div className="panel__body" id="panel-body" hidden={!open}>
        <Slider
          name="offset"
          value={settings.offset}
          min={0}
          max={80}
          step={1}
          suffix="px"
          onChange={(offset) => onChange({ offset })}
        />
        <Slider
          name="scaleStep"
          value={settings.scaleStep}
          min={0}
          max={0.3}
          step={0.01}
          onChange={(scaleStep) => onChange({ scaleStep })}
        />
        <Slider
          name="fadeStep"
          value={settings.fadeStep}
          min={0}
          max={0.9}
          step={0.05}
          onChange={(fadeStep) => onChange({ fadeStep })}
        />
        <Slider
          name="rotateStep"
          value={settings.rotateStep}
          min={-6}
          max={6}
          step={0.5}
          suffix="deg"
          onChange={(rotateStep) => onChange({ rotateStep })}
        />

        <div className="panel__row">
          <button
            type="button"
            className="chip"
            aria-pressed={settings.stackFrom === 'bottom'}
            onClick={() =>
              onChange({ stackFrom: settings.stackFrom === 'top' ? 'bottom' : 'top' })
            }
          >
            from {settings.stackFrom}
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={settings.respectReducedMotion}
            onClick={() => onChange({ respectReducedMotion: !settings.respectReducedMotion })}
          >
            respect motion
          </button>
        </div>

        <button type="button" className="panel__reset mono" onClick={onReset}>
          Reset to defaults
        </button>
      </div>
    </aside>
  );
}

/* ─────────────────────────── page ─────────────────────────── */

function App() {
  const [settings, setSettings] = useState<Settings>(INITIAL);
  const patch = (next: Partial<Settings>) => setSettings((s) => ({ ...s, ...next }));

  return (
    <div className="page">
      <header className="hero">
        <p className="hero__eyebrow mono">stack-on-scroll · v2.0.0</p>
        <h1 className="hero__title">
          Scrolling
          <br />
          <em>deals the deck.</em>
        </h1>
        <p className="hero__lede">
          A React component that pins each card a little lower than the one before it. No
          dependencies, no stylesheet to import, and no JavaScript until you ask for motion.
        </p>

        <div className="hero__install">
          <code className="mono">npm install stack-on-scroll</code>
        </div>

        <nav className="hero__links mono" aria-label="Project links">
          <a href={NPM} target="_blank" rel="noreferrer">
            npm
          </a>
          <a href={REPO} target="_blank" rel="noreferrer">
            GitHub
          </a>
        </nav>

        <p className="hero__cue mono">Scroll to deal</p>
      </header>

      <Controls settings={settings} onChange={patch} onReset={() => setSettings(INITIAL)} />

      <main>
        <StackContainer
          offset={settings.offset}
          scaleStep={settings.scaleStep}
          fadeStep={settings.fadeStep}
          rotateStep={settings.rotateStep}
          stackFrom={settings.stackFrom}
          respectReducedMotion={settings.respectReducedMotion}
          height="100svh"
        >
          {CARDS.map((card) => (
            <SpecCard key={card.id} card={card} offset={settings.offset} />
          ))}
        </StackContainer>
      </main>

      <section className="outro">
        <h2 className="outro__title">That is four props and a hook.</h2>
        <p className="outro__text">
          Everything above is the published package running unmodified. Each control maps one to
          one onto a prop in the readme.
        </p>
        <div className="hero__install">
          <code className="mono">npm install stack-on-scroll</code>
        </div>
      </section>

      <footer className="credits">
        <p className="credits__line">
          Built by{' '}
          <a href={PORTFOLIO} target="_blank" rel="noreferrer">
            Saad Ahmad
          </a>
        </p>
        <p className="credits__meta mono">
          <a href={PORTFOLIO} target="_blank" rel="noreferrer">
            isaadahmad.com
          </a>
          <span aria-hidden="true">·</span>
          <a href={NPM} target="_blank" rel="noreferrer">
            npm
          </a>
          <span aria-hidden="true">·</span>
          <a href={REPO} target="_blank" rel="noreferrer">
            GitHub
          </a>
        </p>
        <p className="credits__small mono">MIT licensed · © {new Date().getFullYear()}</p>
      </footer>
    </div>
  );
}

export default App;
